import React, { useState, useEffect } from 'react';
import { logger } from '@/utils/logger';
import { Lock, Eye, EyeSlash, ChartLineUp, ShieldCheck, Lightning } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Partículas flotantes para el fondo
const FloatingParticles = () => {
  // Generar partículas estáticas para evitar re-renders
  const particles = React.useMemo(() => 
    [...Array(20)].map((_, i) => ({
      id: `particle-${i}`,
      width: Math.random() * 4 + 2,
      height: Math.random() * 4 + 2,
      left: Math.random() * 100,
      top: Math.random() * 100,
      color: i % 2 === 0 ? '#00ff6a' : '#00dcff',
      blur: Math.random() * 10 + 5,
      duration: Math.random() * 10 + 10,
      delay: Math.random() * 5,
    })), []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute rounded-full opacity-20"
          style={{
            width: particle.width + 'px',
            height: particle.height + 'px',
            left: particle.left + '%',
            top: particle.top + '%',
            background: particle.color,
            boxShadow: `0 0 ${particle.blur}px ${particle.color}`,
            animation: `float ${particle.duration}s ease-in-out infinite`,
            animationDelay: `${particle.delay}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          25% { transform: translateY(-20px) translateX(10px); }
          50% { transform: translateY(-10px) translateX(-10px); }
          75% { transform: translateY(-30px) translateX(5px); }
        }
      `}</style>
    </div>
  );
};

export const Login = ({ onLoginSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Auto-login al montar el componente
  useEffect(() => {
    const autoLogin = async () => {
      try {
        // Hacer login automático con la contraseña por defecto desde env
        const defaultPassword = process.env.REACT_APP_DEFAULT_PASSWORD || 'admin123';
        await axios.post(`${API}/auth/login`, { password: defaultPassword }, { withCredentials: true });
        onLoginSuccess();
      } catch (err) {
        logger.error('Error en auto-login:', err);
        setError('Error al conectar con el servidor');
        setLoading(false);
      }
    };
    
    autoLogin();
  }, [onLoginSuccess]);

  // Mostrar pantalla de carga mientras hace auto-login
  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1929 50%, #071018 100%)',
      }}
    >
      {/* Partículas flotantes */}
      <FloatingParticles />
      
      {/* Gradiente radial de fondo */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 0%, rgba(0, 220, 255, 0.1) 0%, transparent 50%)',
        }}
      />

      <div className="w-full max-w-md relative z-10">
        {/* Logo/Header 3D */}
        <div className="text-center mb-8">
          <div 
            className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6 relative group"
            style={{
              background: 'linear-gradient(145deg, rgba(0, 220, 255, 0.2) 0%, rgba(0, 150, 255, 0.1) 100%)',
              border: '2px solid rgba(0, 220, 255, 0.5)',
              boxShadow: '0 0 40px rgba(0, 220, 255, 0.4), 0 20px 40px rgba(0, 0, 0, 0.5), inset 0 2px 0 rgba(255,255,255,0.1)',
              transform: 'perspective(500px) rotateX(5deg)',
            }}
          >
            <ChartLineUp 
              size={40} 
              weight="bold" 
              className="text-gold-400"
              style={{ filter: 'drop-shadow(0 0 10px rgba(0, 220, 255, 0.8))' }}
            />
          </div>
          <h1 
            className="text-4xl font-bold mb-3"
            style={{
              background: 'linear-gradient(180deg, #ffffff 0%, #00dcff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 0 40px rgba(0, 220, 255, 0.3)',
            }}
          >
            KAPITAL
          </h1>
          <p className="text-gray-400 text-base">Análisis Técnico de Activos</p>
        </div>

        {/* Card de carga */}
        <div 
          className="rounded-2xl p-8 relative overflow-hidden"
          style={{
            background: 'linear-gradient(165deg, rgba(15, 25, 45, 0.95) 0%, rgba(10, 18, 35, 0.98) 100%)',
            border: '1px solid rgba(0, 220, 255, 0.3)',
            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.7), 0 0 40px rgba(0, 220, 255, 0.15), inset 0 1px 0 rgba(255,255,255,0.05)',
            backdropFilter: 'blur(20px)',
          }}
        >
          {/* Línea neón superior */}
          <div 
            className="absolute top-0 left-0 right-0 h-[2px]"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, #00dcff 30%, #00ff6a 70%, transparent 100%)',
              boxShadow: '0 0 20px rgba(0, 220, 255, 0.5)',
            }}
          />

          <div className="text-center py-4">
            {loading && !error ? (
              <>
                <div 
                  className="w-16 h-16 mx-auto mb-6 rounded-full border-4 border-gold-500/30 border-t-gold-400 animate-spin"
                  style={{ boxShadow: '0 0 20px rgba(0, 220, 255, 0.3)' }}
                />
                <h2 className="text-xl font-bold text-white mb-2">Conectando...</h2>
                <p className="text-gray-500 text-sm">Accediendo al terminal de trading</p>
              </>
            ) : error ? (
              <>
                <div 
                  className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(145deg, rgba(255, 100, 100, 0.2) 0%, rgba(200, 50, 50, 0.1) 100%)',
                    border: '2px solid rgba(255, 100, 100, 0.5)',
                  }}
                >
                  <span className="text-3xl">⚠️</span>
                </div>
                <h2 className="text-xl font-bold text-red-400 mb-2">Error de Conexión</h2>
                <p className="text-gray-500 text-sm mb-4">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-2 rounded-lg font-medium text-white transition-all"
                  style={{
                    background: 'linear-gradient(145deg, #00dcff 0%, #0099cc 100%)',
                    boxShadow: '0 4px 15px rgba(0, 220, 255, 0.3)',
                  }}
                >
                  Reintentar
                </button>
              </>
            ) : null}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-600">
            © 2026 Analisis Tecnico KAPITAL
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
